import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Option } from '../../../../shared/models/option.model';
import { NotificationService } from '../../../../modules_v2/shared/services/notification.service';
import { Model182Service } from '../../model-182.service';
import { PROVINCIAS_AEAT_NO_RESIDENTES, SINO_AEAT } from '../../tax-catalogs';
import {
  CCAA_182,
  CLAVE_182,
  Model182Declarado,
  Model182Validation,
  NATURALEZA_DECLARADO_182,
  RECURRENCIA_182,
  TIPO_BIEN_182,
  nuevoDeclarado182,
  normalizarDeclarado182
} from '../taxes-182.model';

export interface Model182DeclaradoDialogData {
  declarado: Model182Declarado | null;
  ejercicio: number;
  validaciones: Model182Validation[];
}

export interface Model182DeclaradoDialogResult {
  declarado: Model182Declarado;
}

@Component({
  selector: 'app-model-182-declarado-dialog',
  templateUrl: './model-182-declarado-dialog.component.html',
  styleUrls: ['./model-182-declarado-dialog.component.css'],
  standalone: false
})
export class Model182DeclaradoDialogComponent {

  f: any;
  saving = false;
  validaciones: Model182Validation[];

  readonly claveOptions: Option[] = CLAVE_182;
  readonly provinciaOptions: Option[] = PROVINCIAS_AEAT_NO_RESIDENTES;
  readonly naturalezaOptions: Option[] = NATURALEZA_DECLARADO_182;
  readonly tipoBienOptions: Option[] = TIPO_BIEN_182;
  readonly recurrenciaOptions: Option[] = RECURRENCIA_182;
  readonly ccaaOptions: Option[] = CCAA_182;
  readonly sinoOptions: Option[] = SINO_AEAT;

  static open(dialog: MatDialog, data: Model182DeclaradoDialogData): Observable<Model182DeclaradoDialogResult | null> {
    return dialog.open<Model182DeclaradoDialogComponent, Model182DeclaradoDialogData, Model182DeclaradoDialogResult>(
      Model182DeclaradoDialogComponent, {
      width: '980px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      autoFocus: false,
      data
    }).afterClosed().pipe(map(resultado => resultado || null));
  }

  constructor(
    private dialogRef: MatDialogRef<Model182DeclaradoDialogComponent, Model182DeclaradoDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: Model182DeclaradoDialogData,
    private service: Model182Service,
    private notification: NotificationService
  ) {
    const origen = data.declarado ? normalizarDeclarado182(data.declarado) : nuevoDeclarado182();

    this.f = {
      ...origen,
      especieSN: origen.especie ? 'S' : 'N',
      revocacionSN: origen.revocacion ? 'S' : 'N',
      porcentajeDeduccion: origen.porcentajeDeduccion ?? '',
      porcentajeCcaa: origen.porcentajeCcaa ?? '',
      ejercicioRevocada: origen.ejercicioRevocada ?? ''
    };

    this.validaciones = data.validaciones || [];
  }

  get titulo(): string {
    return this.data.declarado ? 'Editar declarado' : 'Nuevo declarado';
  }

  get subtitulo(): string {
    return `Modelo 182 · ejercicio ${this.data.ejercicio}`;
  }

  get esCalculado(): boolean {
    return this.f.origen === 'CALCULADO';
  }

  mensaje(campo: string): Model182Validation | null {
    return this.validaciones.find(item => item.key === campo) || null;
  }

  get sueltas(): Model182Validation[] {
    return this.validaciones.filter(item => item.key === 'declarados' || !item.key);
  }

  limpiar(campo: string) {
    this.validaciones = this.validaciones.filter(item => item.key !== campo);
  }

  set(campo: string, valor: any) {
    this.f[campo] = valor;
    this.limpiar(campo);
  }

  trackByIndex(i: number) { return i; }

  private numeroONulo(valor: any): number | null {
    if (valor === '' || valor === null || valor === undefined) return null;

    const convertido = Number(String(valor).replace(',', '.'));

    return isNaN(convertido) ? null : convertido;
  }

  private aDto(): Model182Declarado {
    const f = this.f;

    return normalizarDeclarado182({
      uid: f.uid,
      origen: f.origen,
      nif: f.nif,
      nifRepresentante: f.nifRepresentante,
      nombre: f.nombre,
      provincia: f.provincia,
      clave: f.clave,
      porcentajeDeduccion: this.numeroONulo(f.porcentajeDeduccion),
      importe: this.numeroONulo(f.importe) ?? 0,
      especie: f.especieSN === 'S',
      ccaa: f.ccaa,
      porcentajeCcaa: this.numeroONulo(f.porcentajeCcaa),
      naturalezaDeclarado: f.naturalezaDeclarado,
      revocacion: f.revocacionSN === 'S',
      ejercicioRevocada: this.numeroONulo(f.ejercicioRevocada),
      tipoBien: f.tipoBien,
      identificacionBien: f.identificacionBien,
      recurrencia: f.recurrencia,
      nifTitular: f.nifTitular,
      nombreTitular: f.nombreTitular,
      documentos: f.documentos
    });
  }

  guardar() {
    if (this.saving) return;

    const declarado = this.aDto();

    if (declarado.origen !== 'MANUAL') {
      this.dialogRef.close({ declarado });
      return;
    }

    this.saving = true;
    this.dialogRef.disableClose = true;

    this.service.saveRecipient(this.data.ejercicio, declarado)
      .then(guardado => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.dialogRef.close({ declarado: guardado });
      })
      .catch(err => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        const mensajes: string[] = err?.error?.error?.messages;

        this.notification.error(mensajes?.length ? mensajes.join('. ') : 'No se ha podido guardar el declarado');
      });
  }

  cerrar() {
    if (this.saving) return;

    this.dialogRef.close(undefined);
  }
}
